<template>
  <div class="tp-shell" :class="{ embed: embed }">
    <div v-if="loadError" class="empty">{{ loadError }}</div>
    <SectorWarBoard v-else-if="mode === 'sectorWar' && !embed" />
    <ThemePropCenter
      v-else-if="embed"
      ref="center"
      hide-stock-head
      :leader="selectedLeader"
      :leaders="leaders"
      :pack="selectedPack"
      :trade-date="tradeDate"
      :concepts-by-code="conceptsByCode"
      :limit-up-rows="limitUpRows"
      :role-pick="rolePick"
      :sector-key="sectorKey"
      :sector-concept="sectorConcept"
      :sector-source="sectorSource"
      :kingdom-name="kingdomName"
      :sync-dragons="syncDragons"
      :dragon-cards="dragonCards"
      :saved-main-attrs="savedMainAttrs"
      @height-bar="$emit('height-bar', $event)"
      @role-stock="$emit('role-stock', $event)"
    >
      <slot />
    </ThemePropCenter>
    <div v-else class="tp-grid">
      <ThemePropLeaderRail
        :leaders="leaders"
        :selected-id="selectedId"
        :loading="loading"
        :return-meta="returnMeta"
        @select="onSelect"
      />
      <ThemePropCenter
        :leader="selectedLeader"
        :leaders="leaders"
        :pack="selectedPack"
        :trade-date="tradeDate"
      />
      <ThemePropSide :side="selectedPack.side" />
    </div>
  </div>
</template>

<script>
import themeDemo from '@/data/leaderCommandCenter/themePropagateDemo.json'
import stockApi from '@/api/stockApi'
import ThemePropLeaderRail from './ThemePropLeaderRail.vue'
import SectorWarBoard from './SectorWarBoard.vue'
import ThemePropCenter from './ThemePropCenter.vue'
import ThemePropSide from './ThemePropSide.vue'

function conceptSourceKey(item) {
  if (item && item.source === 'KPL') return 'KPL'
  if (item && item.source === '固定') return '固定'
  return 'WC'
}

function conceptLabel(item) {
  const name = item && item.name
  if (!name) return ''
  return `${name}（${conceptSourceKey(item)}）`
}

function conceptKey(item) {
  return `${conceptSourceKey(item)}|${item && item.name}`
}

function normCode6(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6)
}

export default {
  name: 'ThemePropShell',
  components: { ThemePropLeaderRail, SectorWarBoard, ThemePropCenter, ThemePropSide },
  props: {
    leaders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: '' },
    returnMeta: { type: Object, default: () => ({}) },
    conceptsByCode: { type: Object, default: () => ({}) },
    conceptItemsByCode: { type: Object, default: () => ({}) },
    limitUpRows: { type: Array, default: () => [] },
    tradeDate: { type: String, default: '' },
    /** default | sectorWar — 板块战争整页 V3，不改旧 Tab 三栏 */
    mode: { type: String, default: 'default' },
    manualAttrs: { type: Object, default: () => ({}) },
    embed: { type: Boolean, default: false },
    focusCode: { type: String, default: '' },
    focusName: { type: String, default: '' },
    rolePick: { type: String, default: '' },
    sectorKey: { type: String, default: '' },
    sectorConcept: { type: String, default: '' },
    sectorSource: { type: String, default: '' },
    kingdomName: { type: String, default: '' },
    syncDragons: { type: Array, default: () => [] },
    dragonCards: { type: Array, default: () => [] },
    savedMainAttrs: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectedId: '',
      conceptTick: 0,
      returnSeq: 0,
      themeReturn: null,
      once4bCountsByKey: null,
      once4bCountsSeq: 0
    }
  },
  computed: {
    selectedLeader() {
      if (this.embed && this.focusCode) {
        const code = normCode6(this.focusCode)
        const hit = (this.leaders || []).find((row) => normCode6(row && (row.code || row.leaderId)) === code)
        if (hit) return hit
        return {
          code: this.focusCode,
          name: this.focusName || this.focusCode,
          leaderId: this.focusCode
        }
      }
      const id = this.selectedId
      if (!id) return null
      return (this.leaders || []).find((l) => (l.leaderId || l.code) === id) || null
    },
    conceptItems() {
      void this.conceptTick
      const leader = this.selectedLeader
      const code = leader ? normCode6(leader.code) : ''
      const map = this.conceptItemsByCode || {}
      if (!code || !Object.prototype.hasOwnProperty.call(map, code)) return null
      return (map[code] || [])
        .map((item) => ({
          name: item.name,
          source: conceptSourceKey(item),
          label: conceptLabel(item),
          key: conceptKey(item)
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    returnQuery() {
      const items = this.conceptItems
      const leader = this.selectedLeader
      const code = leader ? normCode6(leader.code) : ''
      if (!items || !code || !this.tradeDate) return ''
      return [this.tradeDate, code, items.map((item) => item.key).join('\n')].join('|')
    },
    selectedPack() {
      const leader = this.selectedLeader
      const code = leader ? normCode6(leader.code) : ''
      const items = this.conceptItems
      const known = items != null
      const list = items || []
      const ret = this.themeReturn
      const retReady = !!(ret && ret.code === code && ret.tradeDate === this.tradeDate)
      const byName = retReady ? ret.byName || {} : null
      const recent5ByName = retReady ? ret.recent5ByName || {} : null
      const recent10ByName = retReady ? ret.recent10ByName || {} : null
      const cumByName = retReady ? ret.cumByName || {} : null
      const netByName = retReady ? ret.recent10MainNetByName || {} : null
      const once4bReady = !!(
        this.once4bCountsByKey &&
        this.once4bCountsByKey.code === code &&
        this.once4bCountsByKey.tradeDate === this.tradeDate
      )
      const once4bByName = once4bReady ? this.once4bCountsByKey.byKey || {} : null
      const demoSide = this.demoExtras(leader)
      const pick = (bag, key) => (bag && Object.prototype.hasOwnProperty.call(bag, key) ? bag[key] : null)
      const pickPoolN = (bag, key, pool) => {
        const cell = pick(bag, key)
        if (cell == null) return null
        if (typeof cell === 'number') return pool === 'gt4b' ? cell : null
        if (cell && typeof cell === 'object' && Object.prototype.hasOwnProperty.call(cell, pool)) {
          return cell[pool]
        }
        return null
      }
      if (!known) {
        return {
          loadingConcepts: true,
          loadingReturns: false,
          concepts: [],
          themeRows: [],
          anchorDate: null,
          baseDate: null,
          recent5Start: null,
          recent5End: null,
          recent10Start: null,
          recent10End: null,
          sinceLeaderReturnPct: null,
          catchupPools: demoSide.catchupPools,
          side: demoSide.side
        }
      }
      return {
        loadingConcepts: false,
        loadingReturns: !!(list.length && !retReady),
        concepts: list.map((item) => item.label),
        selectedTheme: (list[0] && list[0].label) || '',
        anchorDate: retReady ? ret.anchorDate : null,
        baseDate: retReady ? ret.baseDate : null,
        recent5Start: retReady ? ret.recent5Start : null,
        recent5End: retReady ? ret.recent5End : null,
        recent10Start: retReady ? ret.recent10Start : null,
        recent10End: retReady ? ret.recent10End : null,
        sinceLeaderReturnPct: null,
        themeRows: list.map((item) => ({
          name: item.label,
          source: item.source,
          sinceLeaderPct: pick(byName, item.key),
          recent5dPct: pick(recent5ByName, item.key),
          recent10dPct: pick(recent10ByName, item.key),
          cumLimitUpN: pick(cumByName, item.key),
          recent10MainNetYi: pick(netByName, item.key),
          indepLimitUpN: null,
          avgRedRatePct: null,
          todayRedRatePct: null,
          todayLimitUpN: null,
          amountMa10: null,
          board4PlusN: pickPoolN(once4bByName, item.key, 'gt4b'),
          board3N: pickPoolN(once4bByName, item.key, 'b3'),
          board2N: pickPoolN(once4bByName, item.key, 'b2'),
          heatLabel: '—',
          heatLevel: 0
        })),
        catchupPools: demoSide.catchupPools,
        side: demoSide.side
      }
    }
  },
  watch: {
    leaders: {
      immediate: true,
      handler(list) {
        const rows = list || []
        if (this.embed) {
          const leader = this.selectedLeader
          if (leader) this.$emit('ensure-concepts', leader)
          return
        }
        if (!rows.length) {
          // 换日加载时列表会空一下。不要因此丢掉当前龙头。
          return
        }
        const still = rows.some((l) => this.sameLeader(l, this.selectedId))
        if (still) {
          const hit = rows.find((l) => this.sameLeader(l, this.selectedId))
          const id = (hit && (hit.leaderId || hit.code)) || ''
          if (id && id !== this.selectedId) this.selectedId = id
        } else if (!this.selectedId) {
          const first = rows[0]
          this.selectedId = (first && (first.leaderId || first.code)) || ''
        } else {
          // 这只不在当前日期的池子里。保存仍留在龙头代码上，退池清理由监控数据负责。
          const first = rows[0]
          this.selectedId = (first && (first.leaderId || first.code)) || ''
        }
        rows.forEach((leader) => this.$emit('ensure-concepts', leader))
      }
    },
    selectedLeader(leader) {
      if (leader) this.$emit('ensure-concepts', leader)
    },
    conceptItemsByCode: {
      deep: true,
      handler() {
        this.conceptTick += 1
      }
    },
    returnQuery: {
      immediate: true,
      handler(q) {
        this.loadThemeReturns(q)
      }
    }
  },
  methods: {
    demoExtras(leader) {
      const by = (themeDemo && themeDemo.byCode) || {}
      const code = leader ? normCode6(leader.code) : ''
      const hit = (code && by[code]) || null
      const emptyPools = { gt4b: { rows: [] }, b3: { rows: [] }, b2: { rows: [] } }
      return {
        catchupPools: (hit && hit.catchupPools) || emptyPools,
        side: (hit && hit.side) || { summary: [], trends: [], alerts: [] }
      }
    },
    sameLeader(leader, id) {
      if (!leader || !id) return false
      if ((leader.leaderId || leader.code) === id) return true
      const a = normCode6(leader.code || leader.leaderId)
      const b = normCode6(id)
      return !!(a && b && a !== '000000' && a === b)
    },
    onSelect(leader) {
      if (!leader) return
      this.selectedId = leader.leaderId || leader.code || ''
    },
    async loadThemeReturns(q) {
      const seq = ++this.returnSeq
      if (!q) {
        this.themeReturn = null
        this.once4bCountsByKey = null
        return
      }
      const parts = q.split('|')
      const tradeDate = parts[0]
      const code = parts[1]
      const concepts = parts.slice(2).join('|').split('\n').filter(Boolean).map((key) => {
        const cut = key.indexOf('|')
        const source = cut >= 0 ? key.slice(0, cut) : 'WC'
        const name = cut >= 0 ? key.slice(cut + 1) : key
        const kept = source === 'KPL' || source === '固定' ? source : 'WC'
        return { name, source: kept }
      })
      try {
        const data = await stockApi.postLeaderThemeReturns({ tradeDate, code, concepts })
        if (seq !== this.returnSeq) return
        const byName = {}
        const recent5ByName = {}
        const recent10ByName = {}
        const cumByName = {}
        const recent10MainNetByName = {}
        for (const row of (data && data.rows) || []) {
          if (!row || !row.name) continue
          const key = conceptKey(row)
          byName[key] = row.sinceLeaderPct
          recent5ByName[key] = row.recent5dPct
          recent10ByName[key] = row.recent10dPct
          cumByName[key] = row.cumLimitUpN
          recent10MainNetByName[key] = row.recent10MainNetYi
        }
        this.themeReturn = {
          code,
          tradeDate,
          anchorDate: (data && data.anchorDate) || null,
          baseDate: (data && data.baseDate) || null,
          recent5Start: (data && data.recent5Start) || null,
          recent5End: (data && data.recent5End) || null,
          recent10Start: (data && data.recent10Start) || null,
          recent10End: (data && data.recent10End) || null,
          byName,
          recent5ByName,
          recent10ByName,
          cumByName,
          recent10MainNetByName
        }
        this.loadOnce4bCounts({
          tradeDate,
          code,
          concepts,
          anchorDate: (data && data.anchorDate) || ''
        })
      } catch (err) {
        if (seq !== this.returnSeq) return
        console.warn('[theme-prop] theme returns failed', err)
        this.themeReturn = { code, tradeDate, anchorDate: null, baseDate: null, recent5Start: null, recent5End: null, recent10Start: null, recent10End: null, byName: {}, recent5ByName: {}, recent10ByName: {}, cumByName: {}, recent10MainNetByName: {} }
        this.once4bCountsByKey = null
      }
    },
    async loadOnce4bCounts({ tradeDate, code, concepts, anchorDate }) {
      const seq = ++this.once4bCountsSeq
      const leader = this.selectedLeader
      if (!tradeDate || !code || !(concepts || []).length) {
        this.once4bCountsByKey = null
        return
      }
      try {
        const data = await stockApi.postLeaderOnce4bCounts({
          tradeDate,
          leaderCode: code,
          concepts,
          exitDate: (leader && leader.exitDate) || '',
          anchorDate: anchorDate || ''
        })
        if (seq !== this.once4bCountsSeq) return
        this.once4bCountsByKey = {
          code,
          tradeDate,
          byKey: (data && data.byKey) || {}
        }
      } catch (err) {
        if (seq !== this.once4bCountsSeq) return
        console.warn('[theme-prop] once-4b counts failed', err)
        this.once4bCountsByKey = { code, tradeDate, byKey: {} }
      }
    },
    startMainAttrPick() {
      const center = this.$refs.center
      if (center) center.startMainAttrPick()
    },
    clearMainAttr(index) {
      const center = this.$refs.center
      if (center) center.clearMainAttr(index)
    },
    clearAllMainAttrs() {
      const center = this.$refs.center
      if (center && typeof center.clearAllMainAttrs === 'function') center.clearAllMainAttrs()
    }
  }
}
</script>

<style scoped lang="scss">
.tp-shell {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}
.tp-shell.embed {
  margin-top: 0;
}
.tp-grid {
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr) minmax(220px, 280px);
  gap: 10px;
  min-height: 640px;
  align-items: stretch;
}
.empty {
  padding: 28px;
  text-align: center;
  color: #8aa0c2;
  background: #fff;
  border-radius: 12px;
}
@media (max-width: 1280px) {
  .tp-grid {
    grid-template-columns: minmax(260px, 300px) minmax(0, 1fr);
  }
  .tp-grid > :nth-child(3) {
    grid-column: 1 / -1;
  }
}
</style>
